import React, { useEffect, useMemo } from 'react';
import { View, Text, Pressable, StyleSheet, ScrollView, Platform } from 'react-native';
import { router } from 'expo-router';
import {
  AlertTriangle,
  Building2,
  ClipboardList,
  Copy,
  FileText,
  Gauge,
  Plus,
  Users,
} from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  useTheme,
  SPACING,
  CONTENT_MAX_WIDTH,
  DESKTOP_CONTENT_MAX_WIDTH,
  FONT_SIZES,
  TYPOGRAPHY,
  withWeight,
} from '../../src/theme';
import { Header } from '@components/layout';
import { EmptyState, ListItem, StatCard, TextLink } from '@components/common';
import { useAuthStore } from '../../src/store/authStore';
import { useAgentClientsStore } from '../../src/store/agentClientsStore';
import { useResponsiveLayout } from '@hooks/useResponsiveLayout';
import { useDesktopShell } from '@hooks/useDesktopShell';
import {
  computeAgentStats,
  recentActivity,
  type RecentActivityEntry,
} from '../../src/utils/agentStats';
import { getReadinessColor, getReadinessLevelFromPercent } from '../../src/utils/readiness';
import { formatRelativeTimeFromMillis } from '../../src/utils/time';

export default function AgentHubScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { isDesktop } = useResponsiveLayout();
  // The agent hub is a work surface (stats, distribution, activity columns),
  // so on the desktop shell it gets the wide content tier, not the 768
  // reading column the user-facing screens use.
  const isDesktopShell = useDesktopShell();
  const { user } = useAuthStore();
  const clients = useAgentClientsStore((s) => s.clients);
  const isLoading = useAgentClientsStore((s) => s.isLoading);
  const fetchClients = useAgentClientsStore((s) => s.fetchClients);

  const insurerName = user?.agentProfile?.insurerName;

  useEffect(() => {
    if (insurerName) void fetchClients(insurerName);
  }, [insurerName, fetchClients]);

  const stats = useMemo(() => computeAgentStats(clients, Date.now()), [clients]);
  const activity = useMemo(() => recentActivity(clients, 5), [clients]);

  if (!user?.agentProfile) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Header showBack title="Agent" />
        <EmptyState
          icon={Users}
          title="Not an insurance agent"
          message="Your account doesn't have agent access. Contact your insurer admin for an invite."
        />
      </View>
    );
  }

  const avgBand =
    stats.avgReadinessPercent !== null
      ? getReadinessLevelFromPercent(stats.avgReadinessPercent)
      : 'none';
  const avgColor = getReadinessColor(avgBand, colors) ?? colors.text;

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Icons-only header with the title in the content, the same shape the
          ported screens use. A centered bold title here was the pre-Design-B
          form. */}
      <Header showBack showAvatar={false} wide={isDesktopShell} />
      <ScrollView
        contentContainerStyle={[
          styles.content,
          { maxWidth: isDesktopShell ? DESKTOP_CONTENT_MAX_WIDTH : CONTENT_MAX_WIDTH },
          { paddingBottom: insets.bottom + 80 },
        ]}
        showsVerticalScrollIndicator={false}>
        <View style={styles.titleBlock}>
          <Text style={[styles.screenTitle, { color: colors.text }]}>Agent</Text>
          {insurerName && (
            <Text style={[styles.screenSubtitle, { color: colors.textSecondary }]}>
              {insurerName}
            </Text>
          )}
        </View>

        {/* Stats */}
        <View
          style={[
            styles.statsRow,
            !isDesktop && styles.statsGrid,
            { borderTopColor: colors.border, borderBottomColor: colors.border },
          ]}>
          <StatCard icon={Building2} value={isLoading ? '—' : stats.total} label="Properties" />
          <StatCard
            icon={Gauge}
            value={
              isLoading
                ? '—'
                : stats.avgReadinessPercent !== null
                  ? `${stats.avgReadinessPercent}%`
                  : '—'
            }
            valueColor={stats.avgReadinessPercent !== null ? avgColor : undefined}
            label="Avg readiness"
          />
          <StatCard
            icon={AlertTriangle}
            value={isLoading ? '—' : stats.atRiskCount}
            valueColor={stats.atRiskCount > 0 ? colors.risk : undefined}
            label="At risk"
          />
          {stats.reportsSentTotal > 0 && (
            <StatCard icon={FileText} value={stats.reportsSentTotal} label="Reports sent" />
          )}
        </View>

        {/* Readiness distribution */}
        {stats.total > 0 && <DistributionBar bandCounts={stats.bandCounts} />}

        {/* Two-column band on desktop, stacked on mobile */}
        <View style={isDesktop ? styles.columns : undefined}>
          <View style={isDesktop ? styles.mainColumn : undefined}>
            <View style={[styles.section, { borderTopColor: colors.border }]}>
              <View style={styles.sectionHeader}>
                <Text style={[styles.sectionTitle, { color: colors.text }]}>Recent activity</Text>
                <TextLink
                  label="View all properties"
                  onPress={() => router.push('/agent/clients')}
                  textStyle={styles.sectionLink}
                />
              </View>
              {activity.length === 0 ? (
                <Text style={[styles.emptyNote, { color: colors.textSecondary }]}>
                  No client checklist activity yet. Activity appears here as owners work their
                  lists.
                </Text>
              ) : (
                activity.map((entry) => <ActivityRow key={entry.structureId} entry={entry} />)
              )}
            </View>
          </View>

          <View style={isDesktop ? styles.sideColumn : undefined}>
            <View style={[styles.section, { borderTopColor: colors.border }]}>
              <ListItem
                icon={Building2}
                label="Client properties"
                onPress={() => router.push('/agent/clients')}
                showDivider
              />
              <ListItem
                icon={ClipboardList}
                label="Manage checklists"
                onPress={() => router.push('/agent/checklists')}
                showDivider
              />
              <ListItem
                icon={Plus}
                label="New checklist"
                onPress={() =>
                  router.push({ pathname: '/agent/checklists/[id]', params: { id: 'new' } })
                }
              />
            </View>

            {/* Invite code */}
            <InviteCodeCard inviteCode={user.agentProfile.inviteCode} />
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

function DistributionBar({
  bandCounts,
}: {
  bandCounts: Record<'none' | 'risk' | 'partial' | 'progress', number>;
}) {
  const { colors } = useTheme();
  const segments = [
    { key: 'progress', label: 'On track', color: colors.progress, count: bandCounts.progress },
    { key: 'partial', label: 'In progress', color: colors.partial, count: bandCounts.partial },
    { key: 'risk', label: 'At risk', color: colors.risk, count: bandCounts.risk },
    { key: 'none', label: 'Not started', color: colors.neutral, count: bandCounts.none },
  ].filter((s) => s.count > 0);

  return (
    <View
      testID="agent-readiness-distribution"
      style={[styles.section, { borderTopColor: colors.border }]}>
      <Text style={[styles.sectionTitle, { color: colors.text }]}>Readiness distribution</Text>
      <View testID="agent-dist-bar" style={styles.distBar}>
        {segments.map((s) => (
          <View
            key={s.key}
            style={[styles.distSegment, { flex: s.count, backgroundColor: s.color }]}
          />
        ))}
      </View>
      <View testID="agent-dist-legend" style={styles.legend}>
        {segments.map((s) => (
          <View key={s.key} style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: s.color }]} />
            <Text style={[styles.legendLabel, { color: colors.textSecondary }]}>
              {s.label} ({s.count})
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function ActivityRow({ entry }: { entry: RecentActivityEntry }) {
  const { colors } = useTheme();
  const pct = entry.total > 0 ? Math.round((entry.completed / entry.total) * 100) : 0;
  const band = getReadinessLevelFromPercent(pct);
  const dot = getReadinessColor(band, colors) ?? colors.textTertiary;
  const when = formatRelativeTimeFromMillis(entry.updatedAtMs, { suffix: 'ago' });

  return (
    <Pressable
      onPress={() => router.push(`/structure/${entry.structureId}`)}
      accessibilityRole="button"
      accessibilityLabel={`Open ${entry.address}`}
      style={({ pressed }) => [styles.activityRow, pressed && { opacity: 0.7 }]}>
      <View style={[styles.activityDot, { backgroundColor: dot }]} />
      <View style={styles.activityText}>
        <Text style={[styles.activityAddress, { color: colors.text }]} numberOfLines={1}>
          {entry.address}
        </Text>
        <Text
          testID="agent-activity-meta"
          style={[styles.activityMeta, { color: colors.textSecondary }]}>
          Completed {entry.completed} of {entry.total} items
        </Text>
      </View>
      {when && (
        <Text
          testID="agent-activity-when"
          style={[styles.activityWhen, { color: colors.textSecondary }]}>
          {when}
        </Text>
      )}
    </Pressable>
  );
}

function InviteCodeCard({ inviteCode }: { inviteCode?: string | null }) {
  const { colors } = useTheme();

  return (
    <View style={[styles.codeCard, { borderTopColor: colors.border }]}>
      {inviteCode ? (
        <>
          <Text style={[styles.codeLabel, { color: colors.textSecondary }]}>Your invite code</Text>
          <View style={styles.codeRow}>
            <Text style={[styles.codeValue, { color: colors.text }]}>{inviteCode}</Text>
            <Pressable
              hitSlop={8}
              onPress={async () => {
                if (Platform.OS === 'web') {
                  await navigator.clipboard.writeText(inviteCode);
                } else {
                  const Clipboard = await import('expo-clipboard');
                  await Clipboard.setStringAsync(inviteCode);
                }
              }}
              accessibilityLabel="Copy invite code">
              <Copy size={18} color={colors.textSecondary} />
            </Pressable>
          </View>
          <Text style={[styles.codeHint, { color: colors.textSecondary }]}>
            Share this with your clients so they can sign up linked to your insurance list.
          </Text>
        </>
      ) : (
        <Text style={[styles.codeHint, { color: colors.textSecondary }]}>
          No invite code assigned. Contact your administrator.
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: {
    width: '100%',
    alignSelf: 'center',
    padding: SPACING.md,
    gap: SPACING.md,
  },
  // Stats sit between two hairlines rather than in three paper tiles.
  statsRow: {
    flexDirection: 'row',
    borderTopWidth: StyleSheet.hairlineWidth,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  statsGrid: {
    flexWrap: 'wrap',
  },
  columns: {
    flexDirection: 'row',
    gap: SPACING.md,
    alignItems: 'flex-start',
  },
  mainColumn: {
    flex: 3,
  },
  sideColumn: {
    flex: 2,
    gap: SPACING.md,
  },
  titleBlock: {
    gap: 2,
  },
  screenTitle: {
    ...TYPOGRAPHY.screenTitle,
  },
  screenSubtitle: {
    ...TYPOGRAPHY.body,
  },
  // Hairline-topped blocks on canvas, not outlined cards.
  section: {
    paddingTop: SPACING.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: SPACING.sm,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    ...TYPOGRAPHY.h4,
    fontSize: FONT_SIZES.md,
  },
  sectionLink: {
    fontSize: 12,
  },
  emptyNote: {
    ...TYPOGRAPHY.bodySmall,
    lineHeight: 20,
  },
  // Same track as ReadinessMeter (4px, radius 2) so the app has one readiness
  // grammar. This was a 10px full-radius bar, which read as a filled surface
  // rather than as data — the only other one left in the app.
  distBar: {
    flexDirection: 'row',
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
    gap: 2,
  },
  distSegment: {
    height: '100%',
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.smd,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  legendLabel: {
    ...TYPOGRAPHY.bodySmall,
  },
  activityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingVertical: SPACING.sm,
  },
  activityDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  activityText: {
    flex: 1,
  },
  activityAddress: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
  activityMeta: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
  },
  activityWhen: {
    ...TYPOGRAPHY.bodySmall,
  },
  codeCard: {
    paddingTop: SPACING.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: SPACING.xs,
  },
  codeLabel: {
    ...TYPOGRAPHY.overline,
  },
  codeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  codeValue: {
    ...TYPOGRAPHY.h1,
    fontSize: FONT_SIZES.xl,
    letterSpacing: 2,
  },
  codeHint: {
    ...TYPOGRAPHY.bodySmall,
  },
});
